import { sourcingEn } from '@/i18n/sourcing';
import { MONEY_SCALE, type PortalOrderPrintDto, type PurchaseOrderDetailDto, describe, isInternalReference } from "@hisense/shared";
import { printHtml } from "./printWindow";

const esc = (s: string) =>
  s.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch]!);

const na = (s: string | null | undefined, empty = "N/A") => {
  const v = (s ?? "").trim();
  return v ? esc(v) : empty;
};

const printDate = (ms: number | null | undefined) =>
  ms ? new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "N/A";

/** Đơn giá 4 chữ số thập phân, tổng 2 — đúng thang tiền của dự án. */
const major4 = (minor: number) => (minor / MONEY_SCALE).toFixed(4);
const major2 = (minor: number) => (minor / MONEY_SCALE).toFixed(2);

/**
 * Bản in Đơn mua hàng (A4), theo bố cục PurchaseOrder.js createPrintableContainer.
 *
 * Không có bản in này thì không có cách nào gửi đơn mua cho nhà cung cấp —
 * hệ thống mới trước đây chỉ xem được trên màn hình.
 *
 * Thông tin nhà cung cấp lấy từ chính chứng từ (supplierName/Address/Phone/
 * Email đã join sẵn trong DTO), không tra lại danh bạ lúc in: một PO in lại sau
 * khi nhà cung cấp đổi địa chỉ phải giữ nguyên địa chỉ tại thời điểm phát hành.
 *
 * Cột LOT chỉ hiện khi có ít nhất một dòng mang số lô — đây là phần P-Process
 * của hệ thống mới, nhà cung cấp không cần thấy cột trống trên mọi đơn thường.
 */
/**
 * Ai đọc bản in. "supplier" là bản gửi nhà cung cấp (kể cả tự tải từ cổng): giữ
 * giá mua, số lượng, ngày giao, điều khoản; bỏ PI/khách hàng đứng sau đơn và yêu
 * cầu mua nội bộ. Máy chủ đã không gửi các trường đó ra cổng; ở đây chỉ để dòng
 * "Ref PI: -" không hiện trống trên giấy.
 */
export type PoPrintAudience = "internal" | "supplier";
/** Bản đầy đủ nội bộ hay bản cổng đều in được: các trường nội bộ là tuỳ chọn. */
export type PoPrintable = PortalOrderPrintDto & Partial<Pick<PurchaseOrderDetailDto, "linkedPiNo" | "sourcingReference">>;

/*
 * Mặc định là BẢN GỬI NHÀ CUNG CẤP (YC-01, 07/10/2026): nhân viên in từ màn nội bộ rồi lưu PDF gửi nhà cung cấp, nên bản mặc định
 * không được mang số PI / RFQ / yêu cầu mua. Bản nội bộ phải chọn tường minh.
 */
export function buildPoHtml(po: PoPrintable, audience: PoPrintAudience = "supplier") {
  const internal = audience === "internal";
  const logoSrc = `${window.location.origin}/brand/hisense-logo.webp`;
  const hasLot = po.items.some((it) => (it.lotNumber ?? "").trim() !== "");
  /**
   * MOQ — số lượng đặt tối thiểu. Hệ cũ in cột này trên đơn gửi nhà cung cấp;
   * đó là con số hai bên đã thoả thuận, và nhà cung cấp đối chiếu nó với số
   * lượng đặt ngay trên tờ giấy. Bỏ đi thì phải hỏi lại qua tin nhắn.
   */
  const hasMoq = po.items.some((it) => (it.moq ?? 0) > 0);
  /**
   * Số cột ĐỨNG TRƯỚC ô Amount: Item, Sub ref, Mô tả, Màu, Cỡ, Qty, Đơn giá,
   * cộng MOQ và Lot nếu có.
   *
   * Trước đây các dòng tổng dùng `colspan = cols - 1` rồi đặt số tiền vào ô kế
   * tiếp — mà ô kế tiếp là cột GHI CHÚ, không phải cột Thành tiền. Nên SUBTOTAL
   * / TAX / GRAND TOTAL của mọi đơn mua hàng đã in ra đều nằm lệch một cột.
   */
  const leadCols = 7 + (hasMoq ? 1 : 0) + (hasLot ? 1 : 0);

  const itemRows = po.items
    .map((it, index) => {
      const zebra = index % 2 === 0 ? "white" : "#f5f5f5";
      return `<tr style="background-color:${zebra};">
        <td class="c mono b">${na(it.productCode, "-")}</td>
        <td class="c muted">${na(it.subRef, "-")}</td>
        <td>${esc(describe(it, po.printLanguage))}</td>
        <td class="c">${na(it.color, "-")}</td>
        <td class="c">${na(it.size, "-")}</td>
        ${hasMoq ? `<td class="c mono">${it.moq ? it.moq.toLocaleString("en-US") : "-"}</td>` : ""}
        ${hasLot ? `<td class="c mono">${na(it.lotNumber, "-")}</td>` : ""}
        <td class="c b">${it.quantity.toLocaleString("en-US")}</td>
        <td class="c mono">${major4(it.unitPrice)}</td>
        <td class="c mono b">${major2(it.totalPrice)}</td>
        <td class="muted">${na(it.remarks, "-")}</td></tr>`;
    })
    .join("");

  // Thuế lưu dạng phần trăm × 100 (12,5% → 1250), không phải tiền.
  const taxPct = (po.taxPercentage ?? 0) / 100;
  const totals: Array<[string, string]> = [["SUBTOTAL", major2(po.subtotal)]];
  if (po.shipping) totals.push(["SHIPPING", major2(po.shipping)]);
  if (po.tax) totals.push([`TAX (${taxPct.toFixed(2)}%)`, major2(po.tax)]);
  const totalsRows = totals
    .map(
      ([k, v]) =>
        `<tr><td colspan="${leadCols}" class="r muted">${esc(k)}</td><td class="c mono">${esc(v)}</td><td></td></tr>`,
    )
    .join("");

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>PO ${esc(po.poNumber)}</title>
<style>
  @page { size: A4; margin: 15mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, "Helvetica Neue", sans-serif; color:#000; font-size:10px; margin:0; }
  @media screen { body { padding: 24px; min-width: 720px; } }
  td { overflow-wrap: anywhere; }
  thead { display: table-header-group; }
  tr { break-inside: avoid; }
  .mono { font-family:"Courier New",monospace; font-variant-numeric:tabular-nums; }
  .c { text-align:center; } .r { text-align:right; } .b { font-weight:bold; }
  .muted { color:#555; }
  .top { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:10px; }
  .logo { height:46px; }
  .title { text-align:right; }
  .title h1 { margin:0; font-size:22px; letter-spacing:2px; }
  .title .no { font-size:14px; font-weight:bold; margin-top:2px; }
  .parties { display:flex; gap:14px; margin:10px 0; }
  .parties > div { flex:1; border:1px solid #000; padding:7px; }
  .parties .h { font-size:8px; letter-spacing:1px; text-transform:uppercase; border-bottom:1px solid #000; margin-bottom:4px; padding-bottom:2px; }
  .parties .n { font-weight:bold; font-size:11px; }
  table { width:100%; border-collapse:collapse; margin-top:8px; }
  th { background:#000; color:#fff; padding:5px 4px; font-size:8px; letter-spacing:.4px; text-transform:uppercase; }
  td { border:1px solid #bbb; padding:4px; vertical-align:top; }
  tfoot td { border:none; padding:3px 4px; font-weight:bold; }
  tfoot tr.grand td { border-top:2px solid #000; font-size:12px; }
  .remarks { margin-top:12px; border:1px solid #bbb; padding:7px; white-space:pre-wrap; }
  .sign { display:flex; gap:40px; margin-top:34px; }
  .sign > div { flex:1; text-align:center; }
  .sign .line { border-top:1px solid #000; margin-top:38px; padding-top:3px; font-size:8px; text-transform:uppercase; letter-spacing:.6px; }
  .foot { margin-top:16px; border-top:1px solid #ddd; padding-top:5px; font-size:8px; color:#777; }
</style></head><body>
  <div class="top">
    <div>
      <img class="logo" src="${logoSrc}" alt="">
      <div class="b" style="margin-top:4px;">HISENSE BUTTON CO., LTD.</div>
    </div>
    <div class="title"><h1>PURCHASE ORDER</h1><div class="no mono">${esc(po.poNumber)}</div></div>
  </div>

  <div class="parties">
    <div>
      <div class="h">Supplier</div>
      <div class="n">${na(po.supplierName)}</div>
      <div>${na(po.supplierAddress, "")}</div>
      <!-- Người nhận đơn: đơn mua hàng gửi tới một CON NGƯỜI cụ thể bên nhà
           cung cấp, và tên đó đã được chốt lên chứng từ lúc phát hành. -->
      <div>${po.supplierContact ? "Attn: " + esc(po.supplierContact) : ""}</div>
      <div>${po.supplierPhone ? "Tel: " + esc(po.supplierPhone) : ""}</div>
      <div>${po.supplierEmail ? esc(po.supplierEmail) : ""}</div>
    </div>
    <div>
      <div class="h">Order details</div>
      <div><b>Date:</b> ${esc(printDate(po.orderDate))}</div>
      <div><b>Expected delivery:</b> ${esc(printDate(po.expectedDeliveryDate))}</div>
      <div><b>Status:</b> ${na(po.status)}</div>
      <div><b>Priority:</b> ${na(po.priority, "-")}</div>
      <div><b>Payment terms:</b> ${na(po.paymentTerms, "-")}</div>
      <div><b>Shipping:</b> ${na(po.shippingMethod, "-")}</div>
      ${po.sourcePProcess ? `<div><b>${esc(sourcingEn.pProcess)}</b></div>` : ""}
      ${internal ? `<div><b>Ref PI:</b> ${na(isInternalReference(po.linkedPiNo) ? "Internal order" : po.linkedPiNo, "-")}</div>` : ""}
      ${internal && po.sourcingReference ? `<div style="overflow-wrap:anywhere"><b>${esc(sourcingEn.printRfq)}:</b> ${na(po.sourcingReference.rfqNo)}</div><div style="overflow-wrap:anywhere"><b>${esc(sourcingEn.printPr)}:</b> ${na(po.sourcingReference.prNo)}</div><div style="overflow-wrap:anywhere"><b>${esc(sourcingEn.printQuote)}:</b> ${na(po.sourcingReference.quoteReference)}</div>` : ""}
    </div>
  </div>

  <table>
    <thead><tr>
      <th>Item</th><th>Sub ref</th><th>Description</th><th>Color</th><th>Size</th>
      ${hasMoq ? "<th>MOQ</th>" : ""}
      ${hasLot ? "<th>Lot</th>" : ""}
      <th>Qty</th><th>Unit price</th><th>Amount</th><th>Remarks</th>
    </tr></thead>
    <tbody>${itemRows}</tbody>
    <tfoot>
      ${totalsRows}
      <tr class="grand"><td colspan="${leadCols}" class="r">GRAND TOTAL · ${esc(po.currency)}</td><td class="c mono">${major2(po.total)}</td><td></td></tr>
    </tfoot>
  </table>

  ${po.remarks ? `<div class="remarks"><b>Remarks:</b> ${esc(po.remarks)}</div>` : ""}

  <div class="sign">
    <div><div class="line">Prepared by</div></div>
    <div><div class="line">Approved by</div></div>
    <div><div class="line">Supplier acknowledgement</div></div>
  </div>

  <div class="foot">Hisense Button · hisensebutton.toankhontech.com · printed ${esc(new Date().toLocaleString("en-GB"))}</div>
</body></html>`;

  return html;
}

export function printPo(po: PoPrintable, audience: PoPrintAudience = "supplier") { printHtml(buildPoHtml(po, audience)); }
